import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_BOTH,
  R2_RETENTION_BOTH,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SPONSOR_ITEM_AUTO,
  SPONSOR_NOTE,
  SPONSOR_PURPOSE,
  SPONSOR_RETENTION_ROW,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  admobOverseasRow,
  admobRow,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  rights,
  table,
} from './common'

/**
 * Lifedeck 개인정보 처리방침.
 *
 * **이 방침에서 가장 조심해야 할 것은 위치다.** 앱은 위치 권한을 쓰지만 **좌표를 서버로 보내지
 * 않는다** — 기기 안에서 운영체제의 주소 변환으로 행정구역 이름을 얻고 그 이름만 전송한다
 * (앱의 `src/location/here.ts` — 소스는 2026-09-20 폐기로 지웠고 git 이력에 남아 있다).
 * 그래서 "위치 정보를 수집한다"고 적으면 사실보다
 * 넓고, "위치를 쓰지 않는다"고 적으면 거짓이다. 실제 흐름 그대로 적는다.
 *
 * **이 설계가 바뀌면(예: 좌표를 서버에 저장하거나 외부 지오코딩 API 를 위탁하면) 이 방침과
 * 스토어 데이터 보안 설문을 같은 커밋에서 되돌려야 한다** (docs/privacy-policy.md).
 *
 * 서버가 있고 로그인이 전제다 — 도감이 계정에 붙어 기기를 바꿔도 따라와야 하기 때문이다.
 * AI 는 쓰지 않는다(업적 판정은 규칙식이다).
 */

const EFFECTIVE_DATE = 'August 20, 2026'
const LAST_REVISED = 'August 26, 2026'
const CRASH_RETENTION = '90 days'
/** 위탁·국외이전 표의 "무슨 광고인가" 칸. 나머지 칸은 common.ts 가 갖는다 */
const ADMOB_PURPOSE = 'Serving banner ads'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Identifying members and keeping them signed in — identifying you by username and password or by a social account, so that your collection belongs to an account',
      'Resetting a password — sending a reset link to a user who has forgotten their password',
      'Storing your collection and syncing it across devices — keeping the entries you wrote, the regions you have visited, and the achievements you unlocked on your account so they survive a change of device',
      'Deciding achievements — comparing your stored entries and regions against fixed rules to work out whether an achievement unlocks',
      'Sending unlock notifications — telling your device the moment an achievement opens',
      'Keeping the service reliable — finding and fixing the places where the app errors',
      SUPPORT_PURPOSE,
      SPONSOR_PURPOSE,
    ],
    note: 'Achievements are decided solely by rules written in advance by a person; no artificial intelligence and no automated profiling is involved. No automated decision is made that has a legal effect on you or a comparably significant impact.',
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: ['The items below are everything the Operator processes.'],
    bullets: [
      '[Required] Account identity — your username, and, if you use a social login, the account identifier and profile name that provider issues',
      '[Required] Password — only if you signed up with a username and password. **Stored as a one-way hash, so not even the Operator can read the original**',
      '[Required] Email address — used only for password reset and account verification. If you use a social login only, this follows whatever that provider supplies',
      '[Required] Installation identifier — a random value created for each device the first time the app runs. It is used only to tell devices apart when the same account receives a notification',
      '[Required] Push token — the address a notification is sent to. It differs per device and becomes invalid when the app is deleted',
      '[Optional] Profile photo — only if you upload one yourself. It is stored on the Operator’s server and you can delete it at any time',
      '[Optional] Entries — the title, note, date, and category you wrote or chose yourself. **They are stored exactly as you wrote them and are shown to no other user**',
      '[Optional] Names of regions you have visited — an administrative region name such as “Seoul, Seongdong-gu”, with the time of first visit and the number of days visited. Latitude and longitude are not stored (Article 4)',
      '[Generated automatically] Unlocked achievements — which achievement opened, and when',
      '[Collected automatically] Device information and advertising identifier used in serving ads — processed by Google (AdMob), which supplies the banner ads, for ad delivery and fraud prevention. The Operator does not receive this information',
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier, app version and build, device model, operating system, error messages, stack traces, and crash traces',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
      SPONSOR_ITEM_AUTO,
    ],
    note: 'If you use a social login, the Operator never receives that provider’s password. Phone number, date of birth, and gender are not requested by any route. Latitude and longitude, movement traces, contacts, address books, and payment details are not collected. There is no feature for attaching a photo to an entry; a photograph reaches the Operator only where you upload a profile photo or attach one to a support message yourself. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected.',
  },
  {
    heading: 'Article 3. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      [
        'Account identity',
        'Until you ask to withdraw (delete your account) — destroyed on request',
        'Your consent',
      ],
      [
        'Password (hash) and email address',
        'Until you ask to withdraw — destroyed on request',
        'Your consent',
      ],
      [
        'Profile photo',
        'Until you delete it or withdraw (destroyed immediately on withdrawal)',
        'Your consent',
      ],
      [
        'Entries (title, note, date, category)',
        'Until you delete them or withdraw (destroyed immediately on withdrawal)',
        'Your consent',
      ],
      [
        'Names of regions visited, and days visited',
        'Until you ask to withdraw — destroyed on request',
        'Your consent',
      ],
      ['Unlocked achievements', 'Until you ask to withdraw — destroyed on request', 'Your consent'],
      [
        'Installation identifier and push token',
        'Until they become invalid through app deletion or turning notifications off, or delivery is confirmed to have failed',
        'Your consent',
      ],
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      [...SUPPORT_RETENTION_ROW],
      [...SPONSOR_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. ${SUPPORT_NOTE} ${SPONSOR_NOTE}`,
  },
  {
    heading: 'Article 4. How location is handled (important)',
    paragraphs: [
      'The service uses your device’s location permission in order to unlock the “first visit to a region” achievements. **The Operator, however, neither collects nor stores your latitude and longitude.**',
      'The flow is this. (1) When you tap “check in here”, or open the app having already granted the location permission (once a day), the device obtains your current coordinates. (2) Those coordinates are turned **on the device** into an administrative region name such as “Seoul, Seongdong-gu”, using the address lookup built into the operating system (CLGeocoder on iOS, Geocoder on Android). (3) The app sends **only that name** to the Operator’s server. The coordinates are gone from the device once this is done and are never transmitted to the server.',
      'The location permission is requested as “while using the app” only; background (“always”) location is never requested. Location is read only while the app is on screen and never while it is closed. The automatic check works only for users who have already granted the permission — the app never raises the permission prompt by itself — does not read again on the same day, and can be switched off in app settings. Even switched off, you can still check in with the “check in here” button.',
      'The address lookup in step (2) is a function of the device operating system, so the map service of Apple Inc. or Google LLC may be involved in performing it. That is not processing entrusted by the Operator and is governed by those companies’ own privacy policies. The Operator receives no information from that step.',
      'If you refuse the location permission, everything else in the service — writing entries, viewing your collection, unlocking achievements — works exactly as before. Only the region achievements stay closed.',
    ],
    note: 'This article describes what the service actually does. Should a change be made in future to store coordinates on the server or to entrust location to an external map API, this policy and the store data safety declaration will be updated together before that change goes live.',
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold. Your entries and your collection are shown to no other user; there is no sharing, comparison, or ranking between users.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      [
        'Kakao Corp.',
        'Authenticating a Kakao account sign-in',
        'Account identifier and profile name',
        'Limited to the moment authentication is processed',
      ],
      [
        'Google LLC',
        'Authenticating a Google account sign-in',
        'Account identifier, name, and email address',
        'Limited to the moment authentication is processed',
      ],
      [
        'Apple Inc.',
        'Authenticating an Apple account sign-in',
        'Account identifier and email address (which you may choose to hide)',
        'Limited to the moment authentication is processed',
      ],
      [
        'Google LLC (Firebase Cloud Messaging)',
        'Delivering achievement unlock notifications',
        'Push token, notification title and body (the name of the achievement unlocked)',
        'Limited to the moment delivery is processed',
      ],
      admobRow(ADMOB_PURPOSE),
      [...CRASHLYTICS_ROW],
      cloudflareRow(R2_ITEMS_BOTH, R2_RETENTION_BOTH),
      [...REVENUECAT_ROW],
    ]),
    note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If the processors change, this policy is updated and notice given in advance. The content of your entries and the names of the regions you have visited are provided to none of these processors.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'Of the processors named in Article 6, Google LLC and Apple Inc. are located outside Korea, and the entrustment transfers personal data abroad as follows. Kakao Corp. is a Korean company, so no transfer abroad is involved there. The entries you wrote and the names of the regions you visited are not transferred abroad.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [
        'Google LLC',
        'The United States and other countries where Google operates',
        'Push token, notification title and body',
        'Sent over HTTPS at the moment a notification is delivered',
        'Delivering achievement unlock notifications',
        'Limited to the moment delivery is processed',
      ],
      [...CRASHLYTICS_OVERSEAS_ROW],
      cloudflareOverseasRow(R2_ITEMS_BOTH, R2_RETENTION_BOTH),
      [...REVENUECAT_OVERSEAS_ROW],
      admobOverseasRow(ADMOB_PURPOSE),
      [
        'Apple Inc.',
        'The United States',
        'Account identifier and email address',
        'Sent over HTTPS at the moment you choose Sign in with Apple',
        'Authenticating an Apple account sign-in',
        'Limited to the moment authentication is processed',
      ],
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'In the app — the entries you wrote can be deleted directly, and your profile photo can be changed or removed there too',
      'Deleting your account — withdrawing from “My account” in the app destroys your entries, visited regions, unlocked achievements, and profile photo immediately',
      'Refusing or withdrawing the location permission — it can be turned off in device settings at any time. Only the region achievements stop unlocking; everything else keeps working',
      'Turning notifications off — revoking the notification permission in device settings stops notifications only; your collection keeps working',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'your entries, the regions you have visited, and the text of support messages',
  ),
  {
    heading:
      'Installation and operation of automatic collection devices, and how to refuse them (including advertising)',
    paragraphs: [
      'The service shows a banner ad at the bottom of the screen, served by Google LLC’s AdMob. The Operator requests non-personalized ads only, which means your behavioural data is not used for targeted advertising. Even for non-personalized ads, however, Google may process device information, an advertising identifier (ADID/IDFA), and coarse IP-based location for ad delivery, fraud prevention, and frequency capping. That information goes from your device directly to Google without passing through the Operator, and Google’s handling of it is governed by the Google Privacy Policy (https://policies.google.com/privacy).',
      'You can refuse this collection by resetting or deleting your advertising identifier in device settings — Android: Settings → Privacy → Ads; iOS: Settings → Privacy & Security → Apple Advertising. Refusing leaves every feature of the service available to you, and non-personalized ads may still appear.',
    ],
    note: 'The Operator uses no cookie, advertising identifier, web beacon, or other automatic collection device for any purpose other than serving those ads, and does not itself use collected personal data for ad targeting or profiling. The location used for advertising is the coarse position Google estimates from your IP address; it is separate from the region names in Article 4 and the two are never combined. The only purchase the service offers is the optional “buy the developer a coffee” sponsorship, which unlocks nothing; payment is carried out by the Apple App Store or Google Play, and the Operator processes only the purchase record described in Articles 3, 5, 7, and 8.',
  },
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 26, 2026 — profile photos and the photographs attached to support messages moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 26, 2026 — the support screen moved out of the app to a page at cottonray.com/support which the app opens inside itself. What is collected, why, and how long it is kept did not change',
    'August 24, 2026 — updated the purposes, items collected, and retention periods following the addition of photo attachments (up to three, chosen by you) and an optional email address to the in-app support screen, which the Operator uses to send a single reply from no-reply@cottonray.com',
  'August 24, 2026 — updated the purposes, items collected, retention periods, entrustment, and notice of transfers abroad following the introduction of the optional “buy the developer a coffee” sponsorship (in-app purchase; receipts verified by RevenueCat)',
    'August 11, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
    'August 20, 2026 — updated the purposes, items collected, retention periods, and how to exercise your rights following the introduction of in-app support messages (feedback only, no contact details taken)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
  ]),
]

export const LIFEDECK_POLICY: PolicyDocument = {
  slug: 'lifedeck',
  title: 'Lifedeck Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for Lifedeck (라이프덱, “the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
